import { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail, usePageMeta } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { Reveal } from "@/components/Reveal";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ type: null, text: "" });
  const [sending, setSending] = useState(false);

  usePageMeta("Contacto · solsdiarias360", "Escríbenos tus dudas, sugerencias o propuestas de productos para solsdiarias360.");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus({ type: null, text: "" });
    try {
      await api.post("/contact", form);
      setStatus({ type: "ok", text: "Mensaje enviado. Te responderemos lo antes posible." });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus({ type: "error", text: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400";

  return (
    <div className="App">
      <Header />
      <main className="bg-slate-50 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14">
          <Reveal>
            <p className="text-amber-600 font-mono-s360 text-xs uppercase tracking-[0.22em] mb-2">Hablemos</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" data-testid="contact-title">Contacto</h1>
            <p className="text-slate-600 text-sm sm:text-base mt-3 leading-relaxed">
              ¿Tienes una duda, una sugerencia o quieres proponernos un producto? Escríbenos y te
              responderemos lo antes posible.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mt-8 space-y-5 shadow-sm" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="text-sm font-semibold text-slate-800 block mb-1.5">Nombre</label>
                <input id="contact-name" data-testid="contact-name-input" required minLength={2} maxLength={100}
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={field} placeholder="Tu nombre" />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-sm font-semibold text-slate-800 block mb-1.5">Email</label>
                <input id="contact-email" type="email" data-testid="contact-email-input" required
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={field} placeholder="tu@email.com" />
              </div>
              <div>
                <label htmlFor="contact-message" className="text-sm font-semibold text-slate-800 block mb-1.5">Mensaje</label>
                <textarea id="contact-message" data-testid="contact-message-input" required minLength={10} maxLength={2000} rows={5}
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={field} placeholder="Cuéntanos..." />
              </div>

              {status.text && (
                <p className={`text-sm flex items-center gap-2 ${status.type === "ok" ? "text-emerald-700" : "text-red-600"}`}
                  data-testid={status.type === "ok" ? "contact-success" : "contact-error"}>
                  {status.type === "ok" && <CheckCircle2 size={16} />} {status.text}
                </p>
              )}

              <button type="submit" disabled={sending} data-testid="contact-submit-button"
                className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-slate-950 font-bold py-3.5 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors">
                <Send size={16} strokeWidth={2.5} /> {sending ? "Enviando..." : "Enviar mensaje"}
              </button>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Al enviar aceptas que tratemos tus datos únicamente para responder a tu mensaje.
                Más información en la política de privacidad.
              </p>
            </form>
          </Reveal>
        </div>
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}
